/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { ClipboardList, FileText, Plus } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { type ChangeEvent, useCallback, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Modal } from '../../components/ui/modal.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type Client } from '../../lib/api/resources.js';
import { useClients } from '../../lib/queries/clinical.js';
import { useSessions } from '../../lib/queries/sessions.js';
import { uiStore } from '../../lib/stores/ui-store.js';
import { formatIst } from '../../lib/utils/datetime.js';
import { initialsOf } from '../../lib/utils/initials.js';
import { formatInr } from '../../lib/utils/money.js';

import { PatientTable } from './patient-table.js';

const ACTIVE_SESSION = new Set(['scheduled', 'in_progress']);

/** Build a `clientId → next upcoming session ISO` map from the therapist's own sessions. */
function useNextSessionByClient(): ReadonlyMap<string, string> {
  const sessions = useSessions();
  return useMemo(() => {
    const map = new Map<string, string>();
    for (const s of sessions.data ?? []) {
      if (!ACTIVE_SESSION.has(s.status)) {
        continue;
      }
      const current = map.get(s.clientId);
      if (current === undefined || s.scheduledStart < current) {
        map.set(s.clientId, s.scheduledStart);
      }
    }
    return map;
  }, [sessions.data]);
}

function ProfileField({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}): JSX.Element {
  return (
    <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-800/50">
      <p className="text-xs text-slate-400">{label}</p>
      <p className="mt-0.5 text-sm font-semibold text-slate-800 dark:text-slate-100">{value}</p>
    </div>
  );
}

function ClientProfile({
  client,
  nextIso,
}: {
  readonly client: Client;
  readonly nextIso: string | undefined;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--accent)] text-base font-bold text-white">
          {initialsOf(client.fullName)}
        </span>
        <div>
          <p className="font-semibold text-slate-800 dark:text-slate-100">{client.fullName}</p>
          <p className="text-xs text-slate-400">
            {client.age !== null ? t('admin.clients.age', { age: client.age }) : ''}
            {client.primaryDiagnosis !== null ? ` • ${client.primaryDiagnosis}` : ''}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <ProfileField label={t('therapist.clients.guardian')} value={client.guardianName ?? '—'} />
        <ProfileField label={t('therapist.clients.phone')} value={client.phone ?? '—'} />
        <ProfileField
          label={t('therapist.clients.therapist')}
          value={client.therapistName ?? '—'}
        />
        <ProfileField label={t('therapist.clients.plan')} value={client.planName ?? '—'} />
        <ProfileField
          label={t('therapist.clients.sessionsPerWeek')}
          value={client.sessionsPerWeek !== null ? String(client.sessionsPerWeek) : '—'}
        />
        <ProfileField
          label={t('therapist.clients.feePerSession')}
          value={client.feePerSessionPaise !== null ? formatInr(client.feePerSessionPaise) : '—'}
        />
        <ProfileField label={t('therapist.clients.status')} value={t(`status.${client.status}`)} />
        <ProfileField
          label={t('therapist.clients.nextLabel')}
          value={
            nextIso !== undefined
              ? formatIst(nextIso, 'EEE d MMM, h:mm a')
              : t('therapist.clients.noUpcoming')
          }
        />
      </div>
      <div className="flex flex-wrap gap-3">
        <Link
          to={`/therapist/clients/${client.id}/intake`}
          className="focus-ring inline-flex items-center gap-2 rounded-xl bg-[var(--accent)] px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90"
        >
          <FileText className="h-4 w-4" aria-hidden="true" />
          {t('therapist.clients.viewIntake')}
        </Link>
        <Link
          to={`/therapist/clients/${client.id}/home-tasks`}
          className="focus-ring inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <ClipboardList className="h-4 w-4" aria-hidden="true" />
          {t('therapist.clients.homeTasks')}
        </Link>
      </div>
    </div>
  );
}

/** Therapist → My Clients (Figma). Caseload cards + a client-profile modal. */
export const TherapistClientsPage = observer(function TherapistClientsPage(): JSX.Element {
  const { t } = useTranslation();
  const clients = useClients();
  const nextByClient = useNextSessionByClient();
  const [selected, setSelected] = useState<Client | null>(null);

  const onSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    uiStore.setSearch(e.target.value);
  }, []);
  const onOpen = useCallback((client: Client) => {
    setSelected(client);
  }, []);
  const onOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setSelected(null);
    }
  }, []);

  const q = uiStore.search.trim().toLowerCase();
  const rows = (clients.data ?? []).filter(
    (c) =>
      q === '' ||
      c.fullName.toLowerCase().includes(q) ||
      (c.primaryDiagnosis ?? '').toLowerCase().includes(q),
  );

  return (
    <div className="p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
            {t('therapist.clients.title')}
          </h1>
          <p className="text-sm text-slate-400">
            {t('therapist.clients.count', { count: clients.data?.length ?? 0 })}
          </p>
        </div>
        <Link
          to="/therapist/schedule?new=1"
          className="focus-ring bg-brand-600 hover:bg-brand-700 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('therapist.clients.newSession')}
        </Link>
      </div>

      <input
        type="search"
        value={uiStore.search}
        onChange={onSearch}
        placeholder={t('therapist.clients.searchPlaceholder')}
        aria-label={t('therapist.clients.searchPlaceholder')}
        className="focus-ring mb-5 h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
      />

      <QueryState
        isLoading={clients.isLoading}
        isError={clients.isError}
        isEmpty={(clients.data ?? []).length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('therapist.clients.empty')}
      >
        <PatientTable rows={rows} nextByClient={nextByClient} onOpen={onOpen} />
      </QueryState>

      <Modal
        open={selected !== null}
        onOpenChange={onOpenChange}
        title={t('therapist.clients.profileTitle')}
        description={selected?.fullName}
        closeLabel={t('common.cancel')}
      >
        {selected !== null && (
          <ClientProfile client={selected} nextIso={nextByClient.get(selected.id)} />
        )}
      </Modal>
    </div>
  );
});
